import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, HStack, Badge,
} from '@chakra-ui/react';
import { useMICUTheme } from '../../lib/micu/MICUThemeProvider';
import { getPatient } from '../../lib/micu/micuDatabase';
import { getMedicationTemplate } from '../../lib/micu/medicationTemplates';
import { calculateIBW } from '../../lib/micu/calculations';

const MedicationDoseInfo = ({ isOpen, onClose, caseId, templateId, currentDose, doseUnit }) => {
  const { isDark } = useMICUTheme();
  const [patient, setPatient] = useState(null);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const p = await getPatient(caseId);
      setPatient(p);
      if (templateId) setTemplate(getMedicationTemplate(templateId));
    })();
  }, [isOpen, caseId, templateId]);

  if (!template) return null;

  const getWeight = () => {
    if (!patient) return null;
    if (template.weightType === 'IBW' && patient.gender && patient.heightInches) {
      return { value: calculateIBW(patient.gender, patient.heightInches), type: 'IBW' };
    }
    if (patient.weightKg) {
      return { value: patient.weightKg, type: 'ABW' };
    }
    return null;
  };

  const weight = getWeight();
  const doseRange = template.doseRange;
  const wbRange = template.weightBasedRange;

  const getDoseStatus = () => {
    if (!currentDose || !doseRange) return null;
    if (currentDose < doseRange.min) return 'below';
    if (currentDose > doseRange.max) return 'above';
    return 'within';
  };

  const doseStatus = getDoseStatus();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} color="micu.text" mx={4}>
        <ModalHeader fontSize="md">{template.name} - Dose Reference</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Standard dose range */}
          {doseRange && (
            <Box mb={4}>
              <Text fontSize="sm" color="micu.textSecondary" mb={1}>Standard Dose Range</Text>
              <Text fontSize="md" fontWeight="bold" color="micu.text">
                {doseRange.min} - {doseRange.max} {doseRange.unit}
              </Text>
            </Box>
          )}

          {/* Weight-based range */}
          {wbRange && weight && (
            <Box mb={4}>
              <Text fontSize="sm" color="micu.textSecondary" mb={1}>Weight-Based Range</Text>
              <Text fontSize="md" fontWeight="bold" color="micu.text">
                {wbRange.min} - {wbRange.max} {wbRange.unit}
              </Text>
              <Text fontSize="xs" color="micu.textMuted" mt={1}>
                = {Math.round(wbRange.min * weight.value * 100) / 100} - {Math.round(wbRange.max * weight.value * 100) / 100} {wbRange.unit.replace('/kg', '')} for {weight.value} kg ({weight.type})
              </Text>
            </Box>
          )}

          {/* Patient weight */}
          {weight && (
            <Box mb={4}>
              <Text fontSize="sm" color="micu.textSecondary" mb={1}>Patient Weight ({weight.type})</Text>
              <Text fontSize="md" fontWeight="bold" color="micu.teal">{weight.value} kg</Text>
              {weight.type === 'IBW' && patient?.heightInches && (
                <Text fontSize="xs" color="micu.textMuted" mt={1}>
                  IBW = {patient.gender === 'M' ? '50' : '45.5'} + 2.3 x ({patient.heightInches}" - 60)
                </Text>
              )}
            </Box>
          )}

          {!weight && template.weightBased && (
            <Text fontSize="sm" color="micu.amber" mb={4}>
              Add patient demographics for weight-based calculations.
            </Text>
          )}

          {/* Current dose status */}
          {currentDose != null && doseStatus && (
            <Box p={3} bg={isDark ? 'micu.bgTertiary' : 'micu.bgSecondary'} borderRadius="md" border="1px solid" borderColor="micu.border">
              <HStack spacing={2}>
                <Text fontSize="sm" color="micu.textSecondary">Current Dose:</Text>
                <Text fontSize="sm" fontWeight="bold" color="micu.text">{currentDose} {doseUnit}</Text>
                <Badge
                  fontSize="2xs"
                  colorScheme={doseStatus === 'within' ? 'green' : doseStatus === 'above' ? 'red' : 'yellow'}
                  variant="subtle"
                >
                  {doseStatus === 'within' ? 'Within range' : doseStatus === 'above' ? 'Above range' : 'Below range'}
                </Badge>
              </HStack>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default MedicationDoseInfo;
